import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, Menu01Icon } from "@hugeicons/core-free-icons";
import type { MenuLinkSize } from "@/app/catalog/navigation";
import { color, ink, type Space } from "@/app/catalog/tokens";
import { inkStyle, space } from "./tokens";

// The phones' menu closes when the screen grows to a desktop's (Tailwind's md).
const DESKTOP_QUERY = "(min-width: 768px)";

// How far the page scrolls before a header that stays is minimized, and how
// close to the top it has to come back to be full size again: apart, so a
// page resting near the threshold doesn't flicker between the two.
const MINIMIZE_AT = 24;
const RESTORE_AT = 4;
// How much the wordmark shrinks then (catalog.css's scales): in a row, and
// above the links in a masthead or stacked bar on desktops.
const WORDMARK_SCALE = 0.8;
const COLUMN_WORDMARK_SCALE = 0.6;
// The room left above and below the minimized bar's content, and around a
// box, ticks or pill frame or a bar with its own background (which keep
// their size).
const ROOM = 8;
const FRAME_ROOM = 6;

// What the navbar shows before its links (its logo or wordmark), to head the
// open menu with. The copy keeps the node's path, so in the builder a click on
// it selects the logo, as a click on any other instance of a node does.
type Brand = { src?: string; alt: string; text?: string; font?: string; node?: string };

const BRAND_PICTURE = "h-10 w-auto max-w-[60%] object-contain";
const BRAND_NAME = "truncate text-lg font-semibold";

function navbarBrand(nav: HTMLElement): Brand | null {
  let node: Element = nav;
  for (let depth = 0; depth < 3 && node.parentElement; depth++) {
    const parent: Element = node.parentElement;
    for (const sibling of Array.from(parent.children)) {
      if (sibling === node) break;
      const path =
        (sibling.hasAttribute("data-node")
          ? sibling
          : sibling.querySelector("[data-node]")
        )?.getAttribute("data-node") ?? undefined;
      const img = sibling.matches("img") ? sibling : sibling.querySelector("img");
      if (img instanceof HTMLImageElement && img.getAttribute("src")) {
        return { src: img.getAttribute("src")!, alt: img.alt, node: path };
      }
      const text = sibling.textContent?.trim().replace(/\s+/g, " ");
      if (text) {
        const font = getComputedStyle(sibling).fontFamily || undefined;
        return { alt: text, text: text.slice(0, 60), font, node: path };
      }
    }
    node = parent;
  }
  return null;
}

// What the open menu shows at its bottom, copied from the page as it opens,
// so every site's menu has it: the site footer's social links (its links
// that show only an icon), the site's first email and phone links (the
// footer's, else the page's) and the footer's copyright line, else one made
// of the year and the navbar's name. What the menu's own links already show
// (a navbar's extras) is left out. Like the logo, a copy keeps its node's
// path, so in the builder a click on it selects the footer's node.
type FooterLink = {
  href: string;
  node?: string;
  label?: string;
  target?: string;
  rel?: string;
  html: string;
  text: string;
};
type MenuFooter = { socials: FooterLink[]; contacts: FooterLink[]; copyright: string };

function menuFooter(nav: HTMLElement, menu: HTMLElement, name: string | undefined): MenuFooter {
  const page = nav.closest("[data-page-theme]");
  // The site's footer is the page's last part: the <footer> landmark on the
  // published site, its section elsewhere.
  const footer = page?.hasAttribute("data-footer") ? page.lastElementChild : null;
  const key = (link: Element) =>
    `${link.getAttribute("href")} ${link.getAttribute("aria-label") ?? link.textContent?.trim()}`;
  const shown = new Set(Array.from(menu.querySelectorAll("[data-menu-links] a"), key));
  const links = (within: Element | null | undefined, selector: string) =>
    Array.from(within?.querySelectorAll<HTMLAnchorElement>(selector) ?? []).filter(
      (link) => !nav.contains(link) && !shown.has(key(link)),
    );
  const copy = (link: HTMLAnchorElement): FooterLink => ({
    href: link.getAttribute("href")!,
    node: link.getAttribute("data-node") ?? undefined,
    label: link.getAttribute("aria-label") ?? undefined,
    target: link.getAttribute("target") ?? undefined,
    rel: link.getAttribute("rel") ?? undefined,
    html: link.innerHTML,
    text: link.textContent?.trim() || link.getAttribute("href")!.replace(/^\w+:/, ""),
  });
  const contacts = ["mailto:", "tel:"].flatMap((scheme) => {
    const selector = `a[href^="${scheme}"]`;
    const link = links(footer, selector)[0] ?? links(page, selector)[0];
    return link ? [copy(link)] : [];
  });
  let copyright = `© ${new Date().getFullYear()}${name ? ` ${name}` : ""}`;
  if (footer) {
    const texts = document.createTreeWalker(footer, NodeFilter.SHOW_TEXT);
    while (texts.nextNode()) {
      if (texts.currentNode.nodeValue?.includes("©")) {
        copyright = texts.currentNode.parentElement!.textContent!.trim();
        break;
      }
    }
  }
  return {
    socials: links(footer, "a[href]")
      .filter((link) => !link.textContent?.trim() && link.firstElementChild)
      .map(copy),
    contacts,
    copyright,
  };
}

type NavMenuProps = HTMLAttributes<HTMLElement> & {
  gap?: Space;
  color?: string;
  menuBackground?: string;
  menuColor?: string;
  menuLabel?: string;
  closeLabel?: string;
  menuOn?: "phones" | "all";
  menuTrigger?: "icon" | "label";
  menuClose?: "icon" | "label";
  menuLinkSize?: MenuLinkSize;
  menuNumbering?: boolean;
  menuStagger?: boolean;
  // How the menu opened before every menu was full screen (#704): a page
  // saved before keeps it until it is saved again (`validatePage`), and it
  // changes nothing.
  menuStyle?: unknown;
};

// A navbar's links: a row on desktops, and on phones a menu button that opens
// them full screen, with the site's social links, contact details and
// copyright at the bottom. With menuOn "all" the button replaces the row on
// desktops too.
//
// It is an island (`navigation.tsx`): on a static page (the preview link, the
// published site) gemi hydrates this component on its own, and its children,
// the links, arrive as static HTML inside a `<gemi-slot>` (display: contents):
// the styles below that reach the links as children reach them through it
// too (`:is(&,&>gemi-slot)>a`). Where the page hydrates (the builder's frame)
// it is a plain component. Both render and open the same markup.
//
// The open menu's transitions are CSS (tailwindcss-animate's classes and
// catalog.css), on the `data-menu-*` hooks, and stop for reduced motion.
const MENU_BUTTON = "-m-2 inline-flex size-11 items-center justify-center rounded-md";

// The menu button as a word (the menu's label): small spaced capitals.
const MENU_WORD =
  "-m-2 inline-flex h-11 items-center rounded-md px-2 text-sm font-medium uppercase tracking-[0.14em]";

const NAV_ROW = "hidden md:flex md:flex-row md:items-center md:gap-[var(--nav-gap)]";

// Class lists are joined as they are, without `cn` (tailwind-merge), which
// would be most of the island's code on a static page.

// The open menu covers the screen: a modal <dialog>, which keeps the focus in
// it and closes on Escape. The page behind it doesn't scroll (catalog.css).
const FULLSCREEN =
  "fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none flex-col overflow-y-auto overscroll-contain border-0 p-0 text-foreground backdrop:bg-transparent";
const FULLSCREEN_OPENING =
  "open:duration-slow open:animate-in open:fade-in-0 motion-reduce:animate-none";

// The links (`a`) large enough to tap comfortably, in the heading font, and
// the call to action (a Button, the `.inline-flex` one) the full width.
// Anything else (a Stack of contact details or social Icons) sits at the
// bottom (catalog.css).
const SHEET_LINKS = [
  "flex flex-1 flex-col items-stretch px-6 pb-8 pt-4 md:px-12",
  "[:is(&,&>gemi-slot)>a:not(.inline-flex)]:flex [:is(&,&>gemi-slot)>a:not(.inline-flex)]:min-h-14 [:is(&,&>gemi-slot)>a:not(.inline-flex)]:items-center [:is(&,&>gemi-slot)>a:not(.inline-flex)]:py-3",
  "[:is(&,&>gemi-slot)>a:not(.inline-flex)]:font-medium [:is(&,&>gemi-slot)>a:not(.inline-flex)]:[font-family:var(--font-heading)]",
  "[:is(&,&>gemi-slot)>.inline-flex]:mt-6 [:is(&,&>gemi-slot)>.inline-flex]:h-14 [:is(&,&>gemi-slot)>.inline-flex]:w-full [:is(&,&>gemi-slot)>.inline-flex]:text-base",
].join(" ");

const LINK_SIZES: Record<MenuLinkSize, string> = {
  md: "[:is(&,&>gemi-slot)>a:not(.inline-flex)]:text-lg",
  lg: "[:is(&,&>gemi-slot)>a:not(.inline-flex)]:text-2xl",
  xl: "[:is(&,&>gemi-slot)>a:not(.inline-flex)]:text-4xl [:is(&,&>gemi-slot)>a:not(.inline-flex)]:tracking-tight",
  "2xl":
    "[:is(&,&>gemi-slot)>a:not(.inline-flex)]:text-[clamp(2.75rem,13vw,6rem)] [:is(&,&>gemi-slot)>a:not(.inline-flex)]:leading-none [:is(&,&>gemi-slot)>a:not(.inline-flex)]:tracking-tight",
};

// The phone menu: the navbar's logo or name and a close button at the top,
// then the links and the call to action, then the menu's footer. It closes on
// a tap on a link (which still leads where it goes), on Escape and on the
// close button, and gives the focus back to the menu button. The links render
// in one place at a time (the row, or the open menu), so each is edited once.
export default function NavMenu({
  menuStyle: _menuStyle,
  menuOn = "phones",
  menuTrigger = "icon",
  menuClose = "icon",
  closeLabel = "Close",
  menuLinkSize = "lg",
  menuNumbering = false,
  menuStagger = false,
  menuColor,
  gap = "lg",
  color: buttonColor,
  menuBackground = "background",
  menuLabel = "Menu",
  children,
  ...props
}: NavMenuProps) {
  const nav = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [copies, setCopies] = useState<{ brand: Brand | null; footer: MenuFooter } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const menuId = useId();
  const phonesOnly = menuOn === "phones";
  const panelColor = color(menuColor ?? buttonColor);

  // A NavLayout with scroll "sticky" or "hide" (navigation.tsx) stays at the
  // top, and is minimized once the page scrolls (data-nav-scrolled); "hide"
  // also slides it away while the page scrolls down and brings it back as it
  // scrolls up (catalog.css). Minimized, the header is its bar's content
  // (the shrunk wordmark, the links, the extras) with ROOM above and below,
  // whatever room its section, container and bar had around it: a framed
  // bar keeps its frame with FRAME_ROOM around it. In a masthead or stacked
  // bar the extras fade and the links come up under the shrunk wordmark
  // (--nav-lift). It only transforms, so nothing on the page moves: the
  // section rises by what it gives up (--nav-rise) and the bar comes down to
  // keep its content in what is left (--nav-drop). This is the navbar's only
  // script, so it lives here. The header is looked up on each scroll, as the
  // builder may change its scroll.
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const bar = nav.current?.closest<HTMLElement>("[data-nav-scroll]");
      const header = bar?.closest("section");
      if (!bar || !header || !nav.current) return;
      const minimized = header.hasAttribute("data-nav-scrolled");
      if (y > MINIMIZE_AT || (minimized && y > RESTORE_AT)) {
        let top = 0;
        for (let node: HTMLElement | null = bar; node && node !== header; ) {
          top += node.offsetTop;
          node = node.offsetParent as HTMLElement | null;
        }
        // The bar's children, by their boxes in the bar (offsets, which
        // transforms don't change); the NavMenu by its <nav>, as on a static
        // page it sits in an island. Hidden ones (a phone's extras) have none.
        const at = (node: HTMLElement) =>
          node.offsetParent === bar ? node.offsetTop : node.offsetTop - bar.offsetTop;
        const menu = nav.current;
        const [wordmark, ...rest] = Array.from(bar.children, (child) =>
          child.contains(menu) ? menu : (child as HTMLElement),
        ).filter((child) => child.offsetHeight > 0);
        const style = getComputedStyle(bar);
        const column =
          style.flexDirection === "column" && /masthead|stacked/.test(bar.dataset.navLayout ?? "");
        let lift = 0;
        let spans = rest.map((child) => [at(child), at(child) + child.offsetHeight]);
        if (wordmark && column) {
          // Shrunk to its top, the quiet extras (an address, a line of text)
          // faded, and the links right under what is left above them: a
          // call to action among the extras stays where it is.
          const kept = rest.filter(
            (child) =>
              child === menu || child.matches("a, button") || child.querySelector("a, button"),
          );
          const above = Math.max(
            at(wordmark) + wordmark.offsetHeight * COLUMN_WORDMARK_SCALE,
            ...kept
              .filter((child) => child !== menu)
              .map((child) => at(child) + child.offsetHeight),
          );
          lift = Math.max(0, at(menu) - above - (parseFloat(style.rowGap) || 0));
          spans = [
            [at(wordmark), at(wordmark) + wordmark.offsetHeight * COLUMN_WORDMARK_SCALE],
            ...kept.map((child) => {
              const from = at(child) - (child === menu ? lift : 0);
              return [from, from + child.offsetHeight];
            }),
          ];
        } else if (wordmark) {
          const shrunk = (wordmark.offsetHeight * (1 - WORDMARK_SCALE)) / 2;
          spans.push([at(wordmark) + shrunk, at(wordmark) + wordmark.offsetHeight - shrunk]);
        }
        // A frame, or a background of the bar's own, keeps its whole box.
        const framed =
          spans.length === 0 ||
          /box|ticks|pill/.test(bar.dataset.navFrame ?? "") ||
          !/^(|transparent|rgba\(0, 0, 0, 0\))$/.test(style.backgroundColor);
        const room = framed ? FRAME_ROOM : ROOM;
        const start = framed ? 0 : Math.min(...spans.map(([from]) => from));
        const end = framed ? bar.offsetHeight : Math.max(...spans.map(([, to]) => to));
        // A section styled to hold no height (its bar hanging out of it, laid
        // over the page as the page's "overlay" lays it) is as tall as the
        // bar: --nav-height, the solid surface's (catalog.css).
        const over = header.offsetHeight < top + bar.offsetHeight;
        const height = over ? top + bar.offsetHeight : header.offsetHeight;
        const rise = Math.max(0, height - (end - start) - 2 * room);
        const drop = rise && room + rise - top - start;
        // Written only when they change: a scroll restyles nothing.
        for (const [name, value] of [
          ["--nav-height", height],
          ["--nav-rise", rise],
          ["--nav-drop", drop],
          ["--nav-lift", lift],
        ] as const) {
          const px = `${Math.round(value)}px`;
          if (header.style.getPropertyValue(name) !== px) header.style.setProperty(name, px);
        }
        header.toggleAttribute("data-nav-over", over);
        if (!minimized) header.setAttribute("data-nav-scrolled", "");
      } else header.removeAttribute("data-nav-scrolled");
      if (bar.dataset.navScroll === "hide" && y > last && y > header.offsetHeight) {
        header.setAttribute("data-nav-hidden", "");
      } else if (y < last) header.removeAttribute("data-nav-hidden");
      last = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // What the open menu copies from the page is read once its links are in
  // it (so the footer leaves out what they already show), before it paints.
  useLayoutEffect(() => {
    const menu = dialog.current;
    if (!menu || !nav.current) return;
    if (open && !menu.open) {
      const brand = navbarBrand(nav.current);
      setCopies({ brand, footer: menuFooter(nav.current, menu, brand?.alt) });
      menu.showModal();
    }
    if (!open && menu.open) menu.close();
  }, [open]);

  useEffect(() => {
    if (!open || !phonesOnly) return;
    const onResize = () => {
      if (window.matchMedia?.(DESKTOP_QUERY)?.matches) setOpen(false);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, phonesOnly]);

  const brand = copies?.brand;
  const footer = copies?.footer;
  return (
    <nav
      {...props}
      ref={nav}
      aria-label={menuLabel}
      data-menu=""
      data-menu-on={phonesOnly ? undefined : "all"}
      className="relative flex items-center"
    >
      <button
        ref={trigger}
        type="button"
        aria-label={menuLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(true)}
        className={`${menuTrigger === "label" ? MENU_WORD : MENU_BUTTON}${phonesOnly ? " md:hidden" : ""}`}
        style={{ color: color(buttonColor) }}
      >
        {menuTrigger === "label" ? (
          menuLabel
        ) : (
          <HugeiconsIcon icon={Menu01Icon} strokeWidth={2} className="size-6" aria-hidden />
        )}
      </button>
      {!open && phonesOnly && (
        <div data-menu-row className={NAV_ROW} style={{ "--nav-gap": space(gap) } as CSSProperties}>
          {children}
        </div>
      )}
      <dialog
        ref={dialog}
        id={menuId}
        aria-label={menuLabel}
        onClose={() => {
          setOpen(false);
          // Without scrolling to it, so a link to a section of the page stays
          // there.
          trigger.current?.focus({ preventScroll: true });
        }}
        onClick={(event) => {
          if ((event.target as Element).closest("a[href]")) setOpen(false);
        }}
        className={`${FULLSCREEN} ${phonesOnly ? "md:hidden max-md:open:flex" : "open:flex"} ${FULLSCREEN_OPENING}`}
        style={{
          background: color(menuBackground),
          ...inkStyle(ink(menuBackground)),
          paddingTop: "env(safe-area-inset-top)",
          paddingRight: "env(safe-area-inset-right)",
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingLeft: "env(safe-area-inset-left)",
        }}
      >
        <div
          className="flex min-h-16 shrink-0 items-center justify-between gap-4 px-6 py-3 md:px-12"
          style={{ color: panelColor }}
        >
          {brand?.src ? (
            <img
              data-menu-brand
              src={brand.src}
              alt={brand.alt}
              data-node={brand.node}
              className={BRAND_PICTURE}
            />
          ) : (
            <span
              data-menu-brand
              data-node={brand?.node}
              className={BRAND_NAME}
              style={{ fontFamily: brand?.font }}
            >
              {brand?.text}
            </span>
          )}
          <button
            type="button"
            data-menu-close
            aria-label={closeLabel}
            onClick={() => setOpen(false)}
            className={
              menuClose === "label"
                ? `${MENU_WORD} -mr-2 shrink-0`
                : "-mr-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md"
            }
          >
            {menuClose === "label" ? (
              closeLabel
            ) : (
              <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} className="size-6" aria-hidden />
            )}
          </button>
        </div>
        <div
          data-menu-links
          data-menu-numbered={menuNumbering || undefined}
          data-menu-stagger={menuStagger || undefined}
          data-menu-color={menuColor ? "" : undefined}
          className={`${SHEET_LINKS} ${LINK_SIZES[menuLinkSize]}`}
          style={menuColor ? ({ "--nav-menu-fg": color(menuColor) } as CSSProperties) : undefined}
        >
          {open && children}
        </div>
        {open && footer && (
          <div
            data-menu-footer
            className="flex shrink-0 flex-col items-start gap-1 px-6 pb-8 pt-5 text-sm md:px-12"
            style={{ color: panelColor }}
          >
            {footer.socials.length > 0 && (
              <div className="-ml-3 flex flex-wrap">
                {footer.socials.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target={link.target}
                    rel={link.rel}
                    aria-label={link.label}
                    data-node={link.node}
                    className="inline-flex size-11 items-center justify-center rounded-md [&_svg]:size-5"
                    dangerouslySetInnerHTML={{ __html: link.html }}
                  />
                ))}
              </div>
            )}
            {footer.contacts.map((link, index) => (
              <a key={index} href={link.href} data-node={link.node} className="py-1">
                {link.text}
              </a>
            ))}
            <p data-menu-copyright className="pt-2 text-xs opacity-70">
              {footer.copyright}
            </p>
          </div>
        )}
      </dialog>
    </nav>
  );
}
